import { useMemo } from "react";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_LIGHT_GRAY,
  BASIC_LINE,
} from "../../../constants/colors";
import { Campaign } from "../../../ducks/campaign";
import {
  formatDate,
  getDatePlusMinutes,
  isDateTimeBefore,
  isFirstIsEarlierHourMinute,
  isSameDate,
} from "../../../utils/date";
import {
  MIN_SELECT_MINUTES,
  Time,
  addMinutes,
  getCurrentTime,
  getTimeRangeByInterval,
  isTimeBefore,
  isTimeEqual,
} from "../../../utils/time";
import { Button } from "../../atoms/Button";
import { PickUpTimeSelect } from "./PickUpTimeSelect";
import { StoreDetail } from "./StoreDetail";

type Props = {
  id?: number;
  isSameDayPickUp: boolean;
  name: string;
  address: string;
  phoneNumber: string;
  service: string;
  location: { lat: number; lng: number };
  isAdvancedPayment: boolean;
  is24Hours: boolean;
  isOvernight: boolean;
  isOrderStopped: boolean;
  isFavorite: boolean;
  onFavoriteClick: (favorite: boolean, onSuccess?: () => void) => void;
  shortestPickUpTime: number;
  onSelectShortestPickUpTime: () => void;
  businessHourOpen: Time;
  businessHourClose: Time;
  onSelectByTime: (hour: number, minute: number) => void;
  selectedDate?: string;
  pickupDate?: string;
  hiddenShortestTime?: boolean;
  campaign: Campaign | null;
};

export const StoreCard: React.FC<Props> = ({
  id,
  isSameDayPickUp,
  name,
  address,
  phoneNumber,
  service,
  location,
  isAdvancedPayment,
  is24Hours,
  isOvernight,
  isOrderStopped,
  isFavorite,
  onFavoriteClick,
  shortestPickUpTime,
  businessHourOpen,
  businessHourClose,
  onSelectShortestPickUpTime,
  onSelectByTime,
  selectedDate,
  hiddenShortestTime,
  campaign,
  pickupDate,
}) => {
  const sameDayPickupSelectableTime = useMemo<Time[]>(() => {
    const currentTime: Time = getCurrentTime();
    let start;
    let end;

    if (is24Hours || isOvernight) {
      start = addMinutes(currentTime, shortestPickUpTime + MIN_SELECT_MINUTES);
      end = { hour: 23, minute: 59 };
    } else if (isTimeBefore(businessHourClose, currentTime)) {
      // 本日の営業時間が終了している場合
      return [];
    } else {
      start = addMinutes(currentTime, shortestPickUpTime + MIN_SELECT_MINUTES);
      end = businessHourClose;
    }

    if (!!campaign) {
      const startDate = new Date(campaign.minSaleStartDate);
      const endDate = new Date(campaign.maxSaleEndDate);
      if (pickupDate && isSameDate(startDate, new Date(pickupDate))) {
        const now = getCurrentTime(true);
        start = { hour: startDate.getHours(), minute: startDate.getMinutes() };
        if (isTimeBefore(start, now)) {
          start = addMinutes(now, MIN_SELECT_MINUTES);
        } else {
          start = addMinutes(start, MIN_SELECT_MINUTES);
        }
      }
      if (pickupDate && isSameDate(endDate, new Date(pickupDate))) {
        end = {
          hour: endDate.getHours(),
          minute: endDate.getMinutes(),
        };
      }
    }

    const intervals = getTimeRangeByInterval(start, end, 10);

    return intervals;
  }, [isSameDayPickUp, campaign, pickupDate]);

  const differentDayPickupSelectableTime = useMemo<Time[]>(() => {
    let start;
    let end;

    if (is24Hours) {
      start = { hour: 0, minute: 0 };
      end = { hour: 23, minute: 59 };
    } else if (isOvernight) {
      let startInterval1 = { hour: 0, minute: 0 };
      let endInterval1 = businessHourClose;
      let shouldIncludeInterval2 = true;
      let campaignInterval2Start: Time | null = null;
      if (!!campaign) {
        const campaignStartDate = new Date(campaign.minSaleStartDate);
        const campaignEndDate = new Date(campaign.maxSaleEndDate);

        const campaignEndDateTime = {
          hour: campaignEndDate.getHours(),
          minute: campaignEndDate.getMinutes(),
        };

        const campaignStartDateTime = {
          hour: campaignStartDate.getHours(),
          minute: campaignStartDate.getMinutes(),
        };

        if (pickupDate && isSameDate(campaignStartDate, new Date(pickupDate))) {
          // キャンペーン開始時刻が早朝クローズ時刻以降の場合、interval1（深夜〜クローズ）は
          // キャンペーン前の時間帯のため表示しない
          if (!isTimeBefore(campaignStartDateTime, businessHourClose)) {
            startInterval1 = endInterval1; // 空のrangeにする
          } else {
            // キャンペーン開始時刻が早朝営業時間内の場合、そこから開始
            startInterval1 = campaignStartDateTime;
          }
          // interval2の開始時刻をキャンペーン開始時刻で調整するために保持
          campaignInterval2Start = campaignStartDateTime;
        }

        endInterval1 = isTimeBefore(businessHourClose, campaignEndDateTime)
          ? businessHourClose
          : campaignEndDateTime;

        if (
          pickupDate &&
          isSameDate(campaignEndDate, new Date(pickupDate)) &&
          (isTimeBefore(campaignEndDateTime, businessHourOpen) ||
            isTimeEqual(campaignEndDateTime, businessHourOpen))
        ) {
          shouldIncludeInterval2 = false;
        }
      }

      const interval1 = getTimeRangeByInterval(
        startInterval1,
        endInterval1,
        10,
      );

      if (!shouldIncludeInterval2) {
        return interval1;
      }

      let startInterval2 = addMinutes(businessHourOpen, shortestPickUpTime);
      // キャンペーン初日の場合、interval2もキャンペーン開始時刻より前の時間を表示しない
      if (
        campaignInterval2Start &&
        isTimeBefore(startInterval2, campaignInterval2Start)
      ) {
        startInterval2 = campaignInterval2Start;
      }
      const interval2 = getTimeRangeByInterval(
        startInterval2,
        { hour: 23, minute: 59 },
        10,
      );

      return [...interval1, ...interval2];
    } else {
      start = addMinutes(
        businessHourOpen,
        shortestPickUpTime + MIN_SELECT_MINUTES,
      );
      end = businessHourClose;
    }

    if (!!campaign) {
      const startDate = new Date(campaign.minSaleStartDate);
      const endDate = new Date(campaign.maxSaleEndDate);
      if (pickupDate && isSameDate(startDate, new Date(pickupDate))) {
        start = { hour: startDate.getHours(), minute: startDate.getMinutes() };
        start = addMinutes(start, MIN_SELECT_MINUTES);
      }
      if (pickupDate && isSameDate(endDate, new Date(pickupDate))) {
        const isEarlier = isFirstIsEarlierHourMinute(
          businessHourClose.hour,
          businessHourClose.minute,
          endDate.getHours(),
          endDate.getMinutes(),
        );
        end =
          isEarlier && !is24Hours
            ? businessHourClose
            : {
                hour: endDate.getHours(),
                minute: endDate.getMinutes(),
              };
      }
    }

    const intervals = getTimeRangeByInterval(start, end, 10);

    return intervals;
  }, [isSameDayPickUp, campaign, pickupDate]);

  const selectableTime = useMemo(() => {
    if (isSameDayPickUp) {
      return sameDayPickupSelectableTime;
    } else {
      return differentDayPickupSelectableTime;
    }
  }, [
    isSameDayPickUp,
    sameDayPickupSelectableTime,
    differentDayPickupSelectableTime,
  ]);

  const isOverToday = useMemo(() => {
    if (is24Hours || !isSameDayPickUp) return false;

    const currentTime = getCurrentTime(false);
    if (isOvernight) {
      return (
        isTimeBefore(
          businessHourClose,
          addMinutes(currentTime, shortestPickUpTime),
        ) && isTimeBefore(currentTime, businessHourOpen)
      );
    }

    return (
      isTimeBefore(currentTime, businessHourOpen) ||
      isTimeBefore(
        businessHourClose,
        addMinutes(currentTime, shortestPickUpTime),
      )
    );
  }, [isSameDayPickUp, campaign]);

  const isReservation = useMemo(() => {
    if (!selectedDate) return false;

    return isDateTimeBefore(
      getDatePlusMinutes(new Date(), shortestPickUpTime + 5),
      selectedDate,
    );
  }, [selectedDate]);

  const selectedTime = useMemo(() => {
    if (!selectedDate) return;

    return {
      hour: Number(formatDate(selectedDate, false, "HH")),
      minute: Number(formatDate(selectedDate, false, "mm")),
    };
  }, [selectedDate]);

  return (
    <StoreCardContainer>
      <StoreDetail
        id={id}
        name={name}
        address={address}
        phoneNumber={phoneNumber}
        service={service}
        location={location}
        isAdvancedPayment={isAdvancedPayment}
        isFavorite={isFavorite}
        onFavoriteClick={onFavoriteClick}
      />

      <StoreSelectTimeContainer>
        {!hiddenShortestTime && (
          <StoreCardSelectShortestTime>
            <StoreCardTimeSelectTextContainer>
              <StoreCardTimeSelectText
                disabled={isOverToday || !isSameDayPickUp || isOrderStopped}
              >
                最短受け取り
                <br />
                お受け取りまで
                <ShortestTimeTextContainer>
                  {shortestPickUpTime}分
                </ShortestTimeTextContainer>
              </StoreCardTimeSelectText>
            </StoreCardTimeSelectTextContainer>

            <ButtonContainer>
              <Button
                btnStyle={
                  isOverToday || isOrderStopped ? "disabled" : "primary"
                }
                text={
                  isOverToday
                    ? "本日は終了しました"
                    : isOrderStopped
                      ? "受付停止中"
                      : "最短でお受け取り"
                }
                size="md"
                disabled={isOverToday || !isSameDayPickUp || isOrderStopped}
                onClick={onSelectShortestPickUpTime}
              />
            </ButtonContainer>
          </StoreCardSelectShortestTime>
        )}

        <PickUpTimeSelect
          selectableTime={selectableTime}
          onSelect={onSelectByTime}
          disabled={isOverToday || isOrderStopped}
          selectedTime={selectedTime}
          defaultOpen={isReservation}
        />
      </StoreSelectTimeContainer>
    </StoreCardContainer>
  );
};

const StoreCardContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${BASIC_LIGHT_GRAY};
  padding: 26px 15px 24px 15px;
`;

const StoreSelectTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-top: 15px;
  border-top: 1px solid ${BASIC_LINE};
`;

const StoreCardSelectShortestTime = styled.div`
  display: flex;
  flex-direction: row;
`;

const StoreCardTimeSelectTextContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StoreCardTimeSelectText = styled.p<{ disabled: boolean }>`
  margin: 0;
  padding: 0;
  text-align: left;
  font-weight: 600;
  color: ${BASIC_DARK_GRAY};
  font-size: 16px;
  line-height: 1.6em;
  opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "auto")};
`;

const ShortestTimeTextContainer = styled.span`
  font-size: 18px;
  font-weight: 600;
  margin-left: 10px;
  color: ${BASIC_DARK_GRAY};
`;

const ButtonContainer = styled.div`
  button {
    width: auto;
    padding: 16px;
    border-radius: 8px;
  }
`;
